import * as React from 'react'
import { ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Spinner, SpinnerSize } from '@fluentui/react'
import { useTranslation } from 'react-i18next'
import useAxios from '../../hooks/useAxios'
import { Destination, IGraphArrayResponse } from '../../context/AxiosContext'
import { driveItemOverviewProperties, formatFolderName } from '../../helpers/documentHelper'
import { DriveItem } from '@microsoft/microsoft-graph-types'
import { Axios, AxiosResponse } from 'axios'
import { PATIENT_RETRY_HEADER } from '../../helpers/graphRetry'
import {
    FlatTree,
    FlatTreeItemProps,
    FlatTreeProps,
    HeadlessFlatTreeItem,
    HeadlessFlatTreeItemProps,
    makeStyles,
    mergeClasses,
    MessageBar,
    MessageBarBody,
    TreeItemValue,
    TreeSelectionValue,
    useHeadlessFlatTree_unstable,
} from '@fluentui/react-components'
import { HeadlessFlatTree, TreeCheckedChangeData } from '@fluentui/react-tree'
import SimpleDocumentFlatTreeItem from './SimpleDocumentFlatTreeItem'
import { DocumentSortOrder } from '../../config/documentConfig'
import { DateTime } from 'luxon'
import FileDocumentSearchBox from '../../v2/pages/File/ViewFileTabs/FileDocumentSearchBox'
import { appInsights, useApplicationStyles } from '../../pages/App'

const useStyles = makeStyles({
    // content-visibility lets the browser skip layout/paint for tree items that are off-screen.
    // contain-intrinsic-size reserves the right amount of space so the scrollbar stays accurate.
    // Applied to every treeitem descendant of FlatTree so large folders (>1000 items) stay responsive.
    virtualizedTree: {
        '& [role="treeitem"]': {
            contentVisibility: 'auto',
            containIntrinsicSize: '0 32px',
        },
    },
})

export enum DocumentSelectionStrategy {
    Single = 'SINGLE',
    HierarchyBackTrack = 'HIERARCHY_BACK_TRACK',
}

// Translation keys for the three load-failure states (HAM-673): no SharePoint link configured,
// folder no longer exists (404) and no access to the folder (403). Overridable per context so
// the CRM tab can phrase them for a relation instead of a dossier.
export type DocumentFlatTreeMessageKeys = {
    noLink: string
    noLinkEdit: string
    folderNotFound: string
    folderNotFoundEdit: string
    folderNoAccess: string
}

const defaultMessageKeys: DocumentFlatTreeMessageKeys = {
    noLink: 'file.pivot.document.error.no_link',
    noLinkEdit: 'file.pivot.document.error.no_link.edit',
    folderNotFound: 'file.pivot.document.error.folder_not_found',
    folderNotFoundEdit: 'file.pivot.document.error.folder_not_found.edit',
    folderNoAccess: 'file.pivot.document.error.folder_no_access',
}

export type DocumentFlatTreeProps = FlatTreeProps & {
    rootId: string
    messageKeys?: DocumentFlatTreeMessageKeys
    // Optional file/dossier reference (e.g. "TST0710") sent along with the prefetch
    // telemetry event so we can correlate request counts to specific dossiers.
    referenceNumber?: string
    sortOrder: DocumentSortOrder
    selectionStrategy: DocumentSelectionStrategy
    selectedOptions: string[]
    onOptionsSelect: (ids: string[], items: FolderDriveItem[]) => void
    setIsLoading?: (isLoading: boolean) => void
    onDriveItemsLoaded?: (allItems: FolderDriveItem[], filteredItems?: FolderDriveItem[]) => void
    setUpdateDriveItems?: (
        setter: (addDriveItems: DriveItem[], removeDriveItems: DriveItem[], filter?: string, sort?: 'asc' | 'desc') => void,
    ) => void
    setReloadDriveItems?: (setter: () => void) => void
    treeItemContent?: (
        flatTree: any,
        props: DocumentFlatTreeProps,
        itemProps: HeadlessFlatTreeItem<FolderDriveItem>,
        isContentLoading: boolean,
        fileCount: number | undefined,
    ) => React.ReactNode
}

export type DocumentFlatTreeItemProps = FlatTreeItemProps &
    DocumentFlatTreeProps & {
        item: FolderDriveItem
        flatTreeHook: HeadlessFlatTree<FolderDriveItem>
        isContentLoading?: boolean
        fileCount?: number
        // size: number | 'small' | 'medium'
    }

export type FolderDriveItem = HeadlessFlatTreeItemProps &
    DriveItem & {
        parent: FolderDriveItem
        displayName: string
        children: FolderDriveItem[]
        childCount?: number
    }

export const mapDriveItem = (item: DriveItem, parentItem: FolderDriveItem, rootId: string): FolderDriveItem => {
    return {
        ...item,
        value: item.id,
        parentValue: item.parentReference.id === rootId ? undefined : item.parentReference.id,
        itemType: item.folder ? 'branch' : 'leaf',
        parent: parentItem,
        displayName: item.folder ? formatFolderName(item.name) : item.name,
        children: [],
        childCount: item.folder?.childCount,
    }
}

const GRAPH_CHILDREN_PAGE_SIZE = 999
// Concurrency for the eager folder-only walk (DocumentCombobox via fetchFolderDriveItemTree).
// Kept low to avoid Graph throttling on dossiers with deep folder hierarchies (HAM-4146).
const GRAPH_FOLDER_TREE_LOAD_CONCURRENCY = 2
// Concurrency for the lazy expand-on-click loads in DocumentFlatTree.
const GRAPH_FOLDER_LAZY_LOAD_CONCURRENCY = 2
// How many layers of folder contents DocumentFlatTree eagerly prefetches on load. A value of
// 3 means we fetch the children of folders at the root-folder level (layer 1), at the next
// level (layer 2), and at the level below that (layer 3) — so items down to layer 4 are
// visible up front and folders at layer 3 already know their own recursive document counts.
// Only contents below layer 3 stay lazy.
const PREFETCH_DEPTH = 3

const PATIENT_RETRY_HEADERS = { SuppressErrors: 'true', [PATIENT_RETRY_HEADER]: 'true' }

const createConcurrencyGate = (limit: number) => {
    let inFlight = 0
    const waiters: Array<() => void> = []
    const next = () => {
        if (inFlight >= limit) {
            return
        }
        const waiter = waiters.shift()
        if (waiter) {
            inFlight++
            waiter()
        }
    }
    return <T,>(task: () => Promise<T>): Promise<T> =>
        new Promise<T>((resolve, reject) => {
            waiters.push(() => {
                task()
                    .then(resolve, reject)
                    .finally(() => {
                        inFlight--
                        next()
                    })
            })
            next()
        })
}

type ConcurrencyGate = <T>(task: () => Promise<T>) => Promise<T>

const fetchDriveItemChildren = (
    axios: Axios,
    id: string,
    nextLink?: string,
    filter?: string,
    gate?: ConcurrencyGate,
    onRequest?: () => void,
): Promise<IGraphArrayResponse<DriveItem>> => {
    const doRequest = (): Promise<IGraphArrayResponse<DriveItem>> => {
        onRequest?.()
        return nextLink
            ? (axios.get(nextLink, { baseURL: Destination.Documents, headers: PATIENT_RETRY_HEADERS }) as Promise<IGraphArrayResponse<DriveItem>>)
            : (axios.get(`/items/${id}/children`, {
                  baseURL: Destination.Documents,
                  params: { $select: driveItemOverviewProperties.join(','), $top: GRAPH_CHILDREN_PAGE_SIZE, $filter: filter },
                  headers: PATIENT_RETRY_HEADERS,
              }) as Promise<IGraphArrayResponse<DriveItem>>)
    }

    const request = gate ? gate(doRequest) : doRequest()

    return request.then((response: IGraphArrayResponse<DriveItem>) => {
        if (response.data['@odata.nextLink']) {
            return fetchDriveItemChildren(axios, id, response.data['@odata.nextLink'], filter, gate, onRequest).then(response2 => ({
                ...response,
                ...response2,
                data: { ...response.data, ...response2.data, value: [...response.data?.value, ...response2.data?.value] },
            }))
        } else {
            return response
        }
    })
}

const mapResponse = (
    axios: Axios,
    driveItem: DriveItem,
    parentItem: FolderDriveItem,
    rootId: string,
    foldersOnly: boolean,
    gate: ConcurrencyGate,
    onRequest?: () => void,
): Promise<FolderDriveItem[]> => {
    return Promise.resolve(mapDriveItem(driveItem, parentItem, rootId)).then(folderDriveItem => {
        if (folderDriveItem.childCount > 0) {
            return (
                fetchDriveItemChildren(axios, folderDriveItem.id, undefined, foldersOnly ? 'folder ne null' : undefined, gate, onRequest)
                    .then(response =>
                        Promise.all(
                            (response.data?.value || []).map(child =>
                                mapResponse(axios, child, folderDriveItem, rootId, foldersOnly, gate, onRequest),
                            ),
                        ),
                    )
                    // Return a flat list [self, ...all descendants]. Each node keeps children:[] from
                    // mapDriveItem and a correct parent pointer; nesting into direct-children is done
                    // once by nestDriveItems in fetchFolderDriveItemTree so the combobox renders each
                    // folder exactly once instead of re-listing every descendant under each ancestor.
                    .then(subtrees => [folderDriveItem, ...subtrees.flat()])
            )
        } else {
            return Promise.resolve([folderDriveItem])
        }
    })
}

export const fetchFolderDriveItemTree = (
    axios: Axios,
    translate: (key: string) => string,
    id: string,
    foldersOnly: boolean = false,
    concurrency: number = GRAPH_FOLDER_TREE_LOAD_CONCURRENCY,
    referenceNumber?: string,
): Promise<FolderDriveItem[]> => {
    const gate = createConcurrencyGate(concurrency)
    const stats = { requests: 0 }
    const startedAt = performance.now()
    return axios
        .get<DriveItem, AxiosResponse<DriveItem>>(`/items/${id}`, {
            baseURL: Destination.Documents,
            params: { $select: driveItemOverviewProperties.join(',') },
            headers: PATIENT_RETRY_HEADERS,
        })
        .then(response => {
            if (response.status === 200) {
                return mapResponse(axios, response.data, undefined, response.data.id, foldersOnly, gate, () => {
                    stats.requests += 1
                }).then(result => {
                    // Nest the flat list so each folder carries only its DIRECT children, then drop
                    // the dossier root itself — the combobox lists selectable subfolders, and the
                    // root's direct children remain the top level (their parent is the root, which
                    // is no longer in the list). This mirrors the pre-loaded (nestDriveItems) shape
                    // the documents screen already feeds to DocumentCombobox.
                    const folders = (nestDriveItems(result) ?? []).filter(item => item.id !== response.data.id)
                    const durationMs = Math.round(performance.now() - startedAt)
                    console.debug(
                        `[DocumentCombobox] folder-tree fetched rootId=${id} foldersOnly=${foldersOnly} requests=${stats.requests} in ${durationMs}ms`,
                    )
                    appInsights.trackEvent({
                        name: 'DocumentCombobox.FetchFolderTree',
                        properties: {
                            referenceNumber,
                            rootId: id,
                            foldersOnly,
                            concurrency,
                            requests: stats.requests,
                            durationMs,
                            items: folders.length,
                            success: true,
                        },
                    })
                    return folders
                })
            }
            return Promise.reject(`${response.status}: ${response.statusText}`)
        })
        .catch(error => {
            const durationMs = Math.round(performance.now() - startedAt)
            const errorStatus = error?.response?.status ?? error?.status
            appInsights.trackEvent({
                name: 'DocumentCombobox.FetchFolderTree',
                properties: {
                    referenceNumber,
                    rootId: id,
                    foldersOnly,
                    concurrency,
                    requests: stats.requests,
                    durationMs,
                    success: false,
                    errorStatus,
                },
            })
            if (errorStatus === 404) {
                return Promise.reject(translate(defaultMessageKeys.folderNotFound) + ' ' + translate(defaultMessageKeys.folderNotFoundEdit))
            } else if (errorStatus === 403) {
                return Promise.reject(translate(defaultMessageKeys.folderNoAccess))
            }
            return Promise.reject(error)
        })
}

// Returns a depth-first traversal so the result matches what FlatTree renders
// (FlatTree walks each parent's childrenValues in source order). Consumers like
// FileDocumentOverview rely on this to compute prev/next over the visible leafs.
const nestDriveItems = (items: FolderDriveItem[] | undefined): FolderDriveItem[] | undefined => {
    if (!items) {
        return items
    }
    const copies = new Map<string, FolderDriveItem>()
    const sourceOrder: FolderDriveItem[] = []
    for (const item of items) {
        const copy = { ...item, children: [] as FolderDriveItem[] }
        copies.set(item.id, copy)
        sourceOrder.push(copy)
    }
    const roots: FolderDriveItem[] = []
    for (const item of sourceOrder) {
        const parentCopy = item.parent?.id ? copies.get(item.parent.id) : undefined
        if (parentCopy) {
            parentCopy.children.push(item)
        } else {
            roots.push(item)
        }
    }
    const result: FolderDriveItem[] = []
    const visit = (node: FolderDriveItem) => {
        result.push(node)
        for (const child of node.children) {
            visit(child)
        }
    }
    roots.forEach(visit)
    return result
}

const sortDriveItems = (
    driveItems: FolderDriveItem[],
    sortOrder: DocumentSortOrder,
    rootId: string,
): ((a: FolderDriveItem, b: FolderDriveItem) => number) => {
    return (a: FolderDriveItem, b: FolderDriveItem) => {
        //Put first level items at the top.
        if (a.parent?.id === rootId && b.parent?.id !== rootId) {
            return -1
        } else if (b.parent?.id === rootId && a.parent?.id !== rootId) {
            return 1
        }

        const parentAIndex = driveItems.indexOf(driveItems.find(item => item.id === a.parent.id))
        const parentBIndex = driveItems.indexOf(driveItems.find(item => item.id === b.parent.id))
        if (parentAIndex < parentBIndex) {
            return parentAIndex
        } else if (parentAIndex > parentBIndex) {
            return parentBIndex
        }

        //Put branches at the top, leafs at the bottom.
        //Specifically used for where folders and documents are children of a folder.
        if (a.itemType === 'branch' && b.itemType !== 'branch') {
            return -1
        } else if (b.itemType === 'branch' && a.itemType !== 'branch') {
            return 1
        }

        //Folders are always sorted alphabetically, regardless of the chosen sort order for files.
        if (a.itemType === 'branch' && b.itemType === 'branch') {
            return a.name.localeCompare(b.name)
        }

        switch (sortOrder) {
            case DocumentSortOrder.NameAsc:
                return a.name.localeCompare(b.name)
            case DocumentSortOrder.NameDesc:
                return b.name.localeCompare(a.name)
            case DocumentSortOrder.ModifiedAsc:
                return DateTime.fromISO(a.lastModifiedDateTime).toMillis() < DateTime.fromISO(b.lastModifiedDateTime).toMillis() ? -1 : 1
            case DocumentSortOrder.ModifiedDesc:
                return DateTime.fromISO(b.lastModifiedDateTime).toMillis() < DateTime.fromISO(a.lastModifiedDateTime).toMillis() ? -1 : 1
        }

        return 0
    }
}

export default function DocumentFlatTree(props: DocumentFlatTreeProps): ReactElement {
    const axios = useAxios()
    const appStyles = useApplicationStyles()
    const styles = useStyles()
    const { t: translate } = useTranslation()
    const messageKeys: DocumentFlatTreeMessageKeys = { ...defaultMessageKeys, ...props.messageKeys }

    const [errorMessage, setErrorMessage] = useState<{ message: string; intent: 'warning' | 'error' }>(undefined)
    const [rootFolder, setRootFolder] = useState<FolderDriveItem>(undefined)
    const [allDriveItems, setAllDriveItems] = useState<FolderDriveItem[]>(undefined)
    // Synchronous mirror of allDriveItems for lookups inside async flows (prefetch chains)
    // that can't wait for React's batched state updater to commit.
    const allDriveItemsRef = useRef<FolderDriveItem[]>(undefined)
    const [filteredDriveItems, setFilteredDriveItems] = useState<FolderDriveItem[]>(undefined)
    const [checkedItems, setCheckedItems] = useState<Map<TreeItemValue, TreeSelectionValue>>(new Map())
    const [openItems, setOpenItems] = useState<string[]>([])
    // loadedFolders: folder ids whose direct children have been fetched. Includes the root id
    // after the initial root-folder list arrives. loadingFolders: folder ids whose children
    // fetch is currently in flight (drives the inline spinner on the row).
    // Refs mirror the state so concurrent prefetch tasks can race-check synchronously.
    const [loadedFolders, setLoadedFolders] = useState<Set<string>>(new Set())
    const [loadingFolders, setLoadingFolders] = useState<Set<string>>(new Set())
    const loadedFoldersRef = useRef<Set<string>>(loadedFolders)
    const loadingFoldersRef = useRef<Set<string>>(loadingFolders)
    const loadEpochRef = useRef(0)
    // One persistent concurrency gate for lazy expand-on-click loads, so a burst of expands
    // is serialised across the component's lifetime rather than per reload.
    const lazyGateRef = useRef<ConcurrencyGate | null>(null)
    if (!lazyGateRef.current) {
        lazyGateRef.current = createConcurrencyGate(GRAPH_FOLDER_LAZY_LOAD_CONCURRENCY)
    }

    const markLoading = useCallback((folderId: string) => {
        const next = new Set(loadingFoldersRef.current)
        next.add(folderId)
        loadingFoldersRef.current = next
        setLoadingFolders(next)
    }, [])
    const unmarkLoading = useCallback((folderId: string) => {
        if (!loadingFoldersRef.current.has(folderId)) {
            return
        }
        const next = new Set(loadingFoldersRef.current)
        next.delete(folderId)
        loadingFoldersRef.current = next
        setLoadingFolders(next)
    }, [])
    const markLoaded = useCallback((folderId: string) => {
        if (loadedFoldersRef.current.has(folderId)) {
            return
        }
        const next = new Set(loadedFoldersRef.current)
        next.add(folderId)
        loadedFoldersRef.current = next
        setLoadedFolders(next)
    }, [])
    const resetLoadState = useCallback((initialLoaded: string[] = []) => {
        loadedFoldersRef.current = new Set(initialLoaded)
        loadingFoldersRef.current = new Set()
        setLoadedFolders(loadedFoldersRef.current)
        setLoadingFolders(loadingFoldersRef.current)
    }, [])
    // Helper that keeps the ref and the state in lockstep so async flows can read the latest
    // items synchronously.
    const replaceAllDriveItems = useCallback((next: FolderDriveItem[] | undefined) => {
        allDriveItemsRef.current = next
        setAllDriveItems(next)
    }, [])
    // An empty folder (childCount = 0) has nothing more to fetch and can be treated as
    // fully loaded immediately — that keeps it out of the prefetch queue and lets its
    // ancestors qualify as fully loaded so they can show a real document count.
    const markEmptyFoldersLoaded = useCallback((folders: FolderDriveItem[]) => {
        const emptyIds = folders.filter(item => item.folder && (item.childCount ?? 0) === 0).map(item => item.id)
        if (emptyIds.length === 0) {
            return
        }
        const next = new Set(loadedFoldersRef.current)
        for (const id of emptyIds) {
            next.add(id)
        }
        loadedFoldersRef.current = next
        setLoadedFolders(next)
    }, [])

    const driveItems: FolderDriveItem[] = useMemo(
        () => (filteredDriveItems ? filteredDriveItems : allDriveItems),
        [filteredDriveItems, allDriveItems],
    )

    // A folder's recursive descendant set is "fully known" only when its own children have
    // been loaded AND every descendant folder is itself fully loaded. Used to decide whether
    // we can show a real document count or must show "?".
    const fullyLoadedFolders = useMemo(() => {
        const result = new Set<string>()
        if (!allDriveItems) {
            return result
        }
        const childrenByParent = new Map<string, FolderDriveItem[]>()
        for (const item of allDriveItems) {
            const parentId = item.parent?.id
            if (!parentId) {
                continue
            }
            const bucket = childrenByParent.get(parentId)
            if (bucket) {
                bucket.push(item)
            } else {
                childrenByParent.set(parentId, [item])
            }
        }
        const visiting = new Set<string>()
        const check = (folderId: string): boolean => {
            if (result.has(folderId)) {
                return true
            }
            if (!loadedFolders.has(folderId)) {
                return false
            }
            if (visiting.has(folderId)) {
                return false
            }
            visiting.add(folderId)
            const children = childrenByParent.get(folderId) ?? []
            for (const child of children) {
                if (child.folder && !check(child.id)) {
                    visiting.delete(folderId)
                    return false
                }
            }
            visiting.delete(folderId)
            result.add(folderId)
            return true
        }
        for (const item of allDriveItems) {
            if (item.folder) {
                check(item.id)
            }
        }
        return result
    }, [allDriveItems, loadedFolders])

    const fileCountByFolderId = useMemo(() => {
        const result = new Map<string, number>()
        if (!allDriveItems) {
            return result
        }
        const childrenByParent = new Map<string, FolderDriveItem[]>()
        for (const item of allDriveItems) {
            const parentId = item.parent?.id
            if (!parentId) {
                continue
            }
            const bucket = childrenByParent.get(parentId)
            if (bucket) {
                bucket.push(item)
            } else {
                childrenByParent.set(parentId, [item])
            }
        }
        const countFiles = (folderId: string): number => {
            const cached = result.get(folderId)
            if (cached !== undefined) {
                return cached
            }
            let count = 0
            const children = childrenByParent.get(folderId)
            if (children) {
                for (const child of children) {
                    count += child.folder ? countFiles(child.id) : 1
                }
            }
            result.set(folderId, count)
            return count
        }
        for (const item of allDriveItems) {
            if (item.folder) {
                countFiles(item.id)
            }
        }
        return result
    }, [allDriveItems])

    const updateDriveItemsFunc = useCallback(
        (addDriveItems?: DriveItem[], removeDriveItems?: DriveItem[]) => {
            const prevItems = allDriveItemsRef.current ?? []
            const removeDriveItemIds = removeDriveItems?.map(driveItem => driveItem.id) || []
            const existingDriveItems = prevItems.filter(prevItem => !removeDriveItemIds.includes(prevItem.id))
            const result = [
                ...existingDriveItems,
                ...(addDriveItems?.map(driveItem =>
                    mapDriveItem(
                        driveItem,
                        prevItems.find(folderDriveItem => driveItem.parentReference.id === folderDriveItem.id),
                        props.rootId,
                    ),
                ) || []),
            ]
            result.sort(sortDriveItems(result, props.sortOrder, props.rootId))
            replaceAllDriveItems(result)
        },
        [props.sortOrder, props.rootId, replaceAllDriveItems],
    )

    // Fetches a folder's direct children (folders + docs), merges into allDriveItems, and
    // updates load-tracking. Promise resolves with the newly mapped children so callers can
    // chain prefetch recursion. Returns [] when the folder is already loaded/loading or when
    // the call fails (patient-retry exhausted) — the resolved value never throws.
    // `stats` is an optional tally that the prefetch path uses to count every Graph HTTP
    // request (including pagination follow-ups) so we can monitor throttling risk.
    const fetchAndMergeChildren = useCallback(
        (folderId: string, epoch: number, stats?: { requests: number }): Promise<FolderDriveItem[]> => {
            if (loadedFoldersRef.current.has(folderId) || loadingFoldersRef.current.has(folderId)) {
                return Promise.resolve([])
            }

            markLoading(folderId)

            return fetchDriveItemChildren(
                axios,
                folderId,
                undefined,
                undefined,
                lazyGateRef.current,
                stats
                    ? () => {
                          stats.requests += 1
                      }
                    : undefined,
            )
                .then(response => {
                    if (epoch !== loadEpochRef.current) {
                        return [] as FolderDriveItem[]
                    }
                    const current = allDriveItemsRef.current
                    const parentItem = current?.find(item => item.id === folderId)
                    if (!current || !parentItem) {
                        return [] as FolderDriveItem[]
                    }
                    const mappedChildren = (response.data?.value || []).map(child => mapDriveItem(child, parentItem, props.rootId))
                    const merged = [...current, ...mappedChildren]
                    merged.sort(sortDriveItems(merged, props.sortOrder, props.rootId))
                    replaceAllDriveItems(merged)
                    markLoaded(folderId)
                    markEmptyFoldersLoaded(mappedChildren)
                    return mappedChildren
                })
                .catch(() => {
                    // Patient retry has already exhausted its budget. Leave the folder in the
                    // unloaded state so a re-open by the user can trigger another fetch.
                    return [] as FolderDriveItem[]
                })
                .finally(() => {
                    if (epoch === loadEpochRef.current) {
                        unmarkLoading(folderId)
                    }
                })
        },
        [axios, markEmptyFoldersLoaded, markLoaded, markLoading, props.rootId, props.sortOrder, replaceAllDriveItems, unmarkLoading],
    )

    // Recursively prefetch a subtree up to `depthRemaining` levels below `folder`. Empty
    // folders short-circuit. Walks via fetchAndMergeChildren so the same gate / load-state /
    // patient-retry path is used as user-triggered loads. The `stats` tally accumulates the
    // Graph request count across the whole prefetch tree.
    const prefetchSubtree = useCallback(
        (folder: FolderDriveItem, depthRemaining: number, epoch: number, stats: { requests: number }): Promise<void> => {
            if (depthRemaining <= 0) {
                return Promise.resolve()
            }
            if (epoch !== loadEpochRef.current) {
                return Promise.resolve()
            }
            if ((folder.childCount ?? 0) === 0) {
                return Promise.resolve()
            }
            return fetchAndMergeChildren(folder.id, epoch, stats).then(children => {
                if (depthRemaining <= 1 || epoch !== loadEpochRef.current) {
                    return undefined
                }
                const subfolders = children.filter(child => child.folder && (child.childCount ?? 0) > 0)
                return Promise.all(subfolders.map(sub => prefetchSubtree(sub, depthRemaining - 1, epoch, stats))).then(() => undefined)
            })
        },
        [fetchAndMergeChildren],
    )

    const reloadDriveItemsFunc = useCallback(() => {
        setErrorMessage(undefined)
        setFilteredDriveItems(undefined)
        resetLoadState()

        if (!props.rootId) {
            replaceAllDriveItems(undefined)
            return
        }

        loadEpochRef.current += 1
        const epoch = loadEpochRef.current

        props.setIsLoading?.(true)

        axios
            .get<DriveItem, AxiosResponse<DriveItem>>(`/items/${props.rootId}`, {
                baseURL: Destination.Documents,
                params: { $select: driveItemOverviewProperties.join(',') },
                headers: PATIENT_RETRY_HEADERS,
            })
            .then(response => {
                if (epoch !== loadEpochRef.current) {
                    return undefined
                }
                if (response.status !== 200) {
                    return Promise.reject(`${response.status}: ${response.statusText}`)
                }
                const root = mapDriveItem(response.data, undefined, response.data.id)
                setRootFolder(root)
                return fetchDriveItemChildren(axios, props.rootId, undefined, 'folder ne null', lazyGateRef.current).then(childrenResponse => {
                    if (epoch !== loadEpochRef.current) {
                        return undefined
                    }
                    const rootFolders = (childrenResponse.data?.value || []).map(child => mapDriveItem(child, root, props.rootId))
                    rootFolders.sort(sortDriveItems(rootFolders, props.sortOrder, props.rootId))
                    replaceAllDriveItems(rootFolders)
                    resetLoadState([props.rootId])
                    markEmptyFoldersLoaded(rootFolders)
                    props.setIsLoading?.(false)
                    // Eagerly prefetch the first PREFETCH_DEPTH layers below each root folder
                    // so the user sees real document counts and depth-3 content without having
                    // to click. Deeper folders stay lazy.
                    if (rootFolders.length > 0) {
                        const prefetchStart = performance.now()
                        const stats = { requests: 0 }
                        Promise.all(rootFolders.map(rf => prefetchSubtree(rf, PREFETCH_DEPTH, epoch, stats))).then(() => {
                            if (epoch !== loadEpochRef.current) {
                                return
                            }
                            const durationMs = Math.round(performance.now() - prefetchStart)
                            console.debug(`[DocumentFlatTree] prefetch done depth=${PREFETCH_DEPTH} requests=${stats.requests} in ${durationMs}ms`)
                            appInsights.trackEvent({
                                name: 'DocumentFlatTree.Prefetch',
                                properties: {
                                    referenceNumber: props.referenceNumber,
                                    rootId: props.rootId,
                                    depth: PREFETCH_DEPTH,
                                    requests: stats.requests,
                                    durationMs,
                                    rootFolders: rootFolders.length,
                                },
                            })
                        })
                    }
                })
            })
            .catch(error => {
                if (epoch !== loadEpochRef.current) {
                    return
                }
                replaceAllDriveItems([])
                props.setIsLoading?.(false)
                const errorStatus = error?.response?.status ?? error?.status
                if (errorStatus === 404) {
                    setErrorMessage({
                        message: translate(messageKeys.folderNotFound) + ' ' + translate(messageKeys.folderNotFoundEdit),
                        intent: 'warning',
                    })
                } else if (errorStatus === 403) {
                    setErrorMessage({ message: translate(messageKeys.folderNoAccess), intent: 'error' })
                } else {
                    setErrorMessage({ message: error?.toString?.() ?? String(error), intent: 'error' })
                }
            })
    }, [
        axios,
        markEmptyFoldersLoaded,
        messageKeys,
        prefetchSubtree,
        props.referenceNumber,
        props.rootId,
        props.setIsLoading,
        props.sortOrder,
        replaceAllDriveItems,
        resetLoadState,
        translate,
    ])

    const loadFolderChildren = useCallback(
        (folderId: string) => {
            fetchAndMergeChildren(folderId, loadEpochRef.current)
        },
        [fetchAndMergeChildren],
    )

    useEffect(() => {
        setFilteredDriveItems(prevItems => {
            if (prevItems) {
                const result = [...prevItems]
                result.sort(sortDriveItems(result, props.sortOrder, props.rootId))
                return result
            }
            return prevItems
        })
        const prevAll = allDriveItemsRef.current
        if (prevAll) {
            const result = [...prevAll]
            result.sort(sortDriveItems(result, props.sortOrder, props.rootId))
            replaceAllDriveItems(result)
        }
    }, [props.sortOrder, props.rootId, replaceAllDriveItems])

    useEffect(() => {
        if (loadingFolders.size > 0) {
            return
        }
        props.onDriveItemsLoaded?.(nestDriveItems(allDriveItems), nestDriveItems(filteredDriveItems))
    }, [allDriveItems, filteredDriveItems, loadingFolders])

    useEffect(() => {
        props.setUpdateDriveItems?.((addDriveItems: DriveItem[], removeDriveItems: DriveItem[]) =>
            updateDriveItemsFunc(addDriveItems, removeDriveItems),
        )
        props.setReloadDriveItems?.(() => reloadDriveItemsFunc())

        return () => props.setUpdateDriveItems?.(() => {}) // Cleanup
    }, [])

    const flatTree = useHeadlessFlatTree_unstable(driveItems, {
        selectionMode: 'multiselect',
        onCheckedChange: (_, data: TreeCheckedChangeData) => {
            let newCheckedItems: Map<TreeItemValue, TreeSelectionValue> = new Map(checkedItems)
            let newSelectedDriveItemIds: string[] = []
            let newSelectedDriveItems: FolderDriveItem[] = []

            switch (props.selectionStrategy) {
                case DocumentSelectionStrategy.Single: {
                    if (data.checked) {
                        newCheckedItems.set(data.value, data.checked)
                    } else {
                        newCheckedItems.delete(data.value)
                    }
                    newSelectedDriveItemIds = Array.from(newCheckedItems.keys()) as string[]
                    newSelectedDriveItems = Array.from(newCheckedItems)
                        .filter(([, checkedValue]) => checkedValue === true)
                        .flatMap(([checkedItem]) => allDriveItems.filter(item => checkedItem === item.id))

                    break
                }
                case DocumentSelectionStrategy.HierarchyBackTrack: {
                    newCheckedItems = new Map(data.checkedItems)

                    newSelectedDriveItemIds = Array.from(data.checkedItems)
                        .filter(
                            ([checkedItem, checkedValue]) =>
                                allDriveItems.find(item => item.value === checkedItem)?.itemType === 'leaf' && checkedValue === true,
                        )
                        .map(([item]) => item as string)
                    newSelectedDriveItems = allDriveItems.filter(item => newSelectedDriveItemIds.includes(item.id))

                    break
                }
            }

            props.onOptionsSelect?.(newSelectedDriveItemIds, newSelectedDriveItems)
        },
        checkedItems: checkedItems,
        openItems: openItems,
        onOpenChange: (_, data) => {
            const folderId = data.value.toString()
            if (data.open) {
                setOpenItems(oldItems => [...new Set([...oldItems, folderId])])
                loadFolderChildren(folderId)
            } else {
                setOpenItems(oldItems => oldItems.filter(item => item !== folderId))
            }
        },
    })

    useEffect(() => {
        reloadDriveItemsFunc()
    }, [props.rootId])

    useEffect(() => {
        if (allDriveItems) {
            switch (props.selectionStrategy) {
                case DocumentSelectionStrategy.Single: {
                    setCheckedItems(
                        new Map(
                            props.selectedOptions
                                ?.map(value => allDriveItems.find(item => item.value === value))
                                .filter(Boolean)
                                .map(value => [value.id, true as TreeSelectionValue]),
                        ),
                    )
                    break
                }
                case DocumentSelectionStrategy.HierarchyBackTrack: {
                    setCheckedItems(
                        new Map(
                            props.selectedOptions
                                ?.map(value => allDriveItems.find(item => item.value === value))
                                .filter(Boolean)
                                .flatMap(value => {
                                    const hierarchy: [TreeItemValue, TreeSelectionValue][] = []
                                    hierarchy.push([value.id, true])
                                    let parentItem = allDriveItems.find(item => item.value === value.parentValue)
                                    while (parentItem !== undefined) {
                                        hierarchy.push([parentItem.id, 'mixed'])
                                        parentItem = allDriveItems.find(item => item.value === parentItem.parentValue)
                                    }
                                    return hierarchy
                                }) || [],
                        ),
                    )
                    break
                }
            }
        }
    }, [driveItems, props.selectedOptions])

    if (!props.rootId) {
        return (
            <MessageBar intent={'warning'}>
                <MessageBarBody>
                    {translate(messageKeys.noLink)} {translate(messageKeys.noLinkEdit)}
                </MessageBarBody>
            </MessageBar>
        )
    }

    if (errorMessage) {
        return <MessageBar intent={errorMessage.intent}>{errorMessage.message}</MessageBar>
    }

    if (!allDriveItems) {
        return <Spinner label={translate('base.loading')} size={SpinnerSize.xSmall} labelPosition="right" />
    }

    return (
        <div className={mergeClasses(appStyles.flexColumn, appStyles.gapS)}>
            <FileDocumentSearchBox
                {...props}
                placeholderKey={'file.pivot.document.search.placeholder'}
                componentProps={{ appearance: 'underline' }}
                rootFolder={rootFolder}
                driveItems={allDriveItems}
                setFilteredDriveItems={searchResults => {
                    const sortedResults = searchResults.sort(sortDriveItems(searchResults, props.sortOrder, props.rootId))
                    setFilteredDriveItems(sortedResults)
                    props.onOptionsSelect?.([], [])
                    setOpenItems(sortedResults.filter(item => item.itemType === 'branch').map(item => item.id))
                }}
                reset={() => {
                    setFilteredDriveItems(undefined)
                    props.onOptionsSelect?.([], [])
                    setOpenItems([])
                    reloadDriveItemsFunc()
                }}
            />
            {allDriveItems.length === 0 && (
                <MessageBar intent={'info'}>
                    <MessageBarBody>
                        {translate('file.pivot.document.empty', { defaultValue: 'Dit dossier bevat nog geen mappen of documenten.' })}
                    </MessageBarBody>
                </MessageBar>
            )}
            <FlatTree
                {...props}
                {...flatTree.getTreeProps()}
                aria-label={'FileDocumentTree'}
                className={mergeClasses(styles.virtualizedTree, props.className)}
            >
                {Array.from(flatTree.items(), flatTreeItem => {
                    const treeItemProps = flatTreeItem.getTreeItemProps()
                    const folderId = treeItemProps.value as string
                    const isContentLoading = loadingFolders.has(folderId)
                    // Show a number only when the recursive document count is known with
                    // certainty — that requires this folder and every descendant folder to be
                    // loaded. Otherwise pass undefined so the row can render "?".
                    const fileCount: number | undefined = fullyLoadedFolders.has(folderId) ? (fileCountByFolderId.get(folderId) ?? 0) : undefined
                    return (
                        props.treeItemContent?.(flatTree, props, flatTreeItem, isContentLoading, fileCount) || (
                            <SimpleDocumentFlatTreeItem
                                key={flatTreeItem.value}
                                {...treeItemProps}
                                {...props}
                                item={treeItemProps}
                                flatTreeHook={flatTree}
                                itemType={flatTreeItem.itemType}
                                size={props.size}
                                isContentLoading={isContentLoading}
                                fileCount={fileCount}
                            />
                        )
                    )
                })}
            </FlatTree>
        </div>
    )
}
